import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Calendar as CalendarIcon,
  CalendarCheck,
  Check
} from "@dreamhub/designsystem/icons";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import Calendar from "@/components/ui/calendar";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";
import { SingleSelect } from "@/components/controls/SingleSelect";
import { cn } from "@/utils/utils";
import { DateShift, SHIFT_PRESETS, ShiftUnit } from "./types";

const SHIFT_UNITS: ShiftUnit[] = ["days", "weeks", "months", "years"];

export type ShiftDatesSelection =
  | { kind: "preset"; shift: DateShift }
  | { kind: "custom"; shift: DateShift }
  | { kind: "date"; date: Date };

interface ShiftDatesDropdownProps {
  trigger: JSX.Element;
  selection: ShiftDatesSelection | null;
  onSelect: (selection: ShiftDatesSelection) => void;
  align?: "start" | "end";
  side?: "top" | "bottom";
}

const ListItem: React.FC<{
  isSelected: boolean;
  icon?: JSX.Element;
  onClick: () => void;
  dataTest: string;
  children: React.ReactNode;
}> = ({ isSelected, icon, onClick, dataTest, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-2 h-9 w-full rounded-lg px-2 text-left transition-colors hover:bg-surface-hover-soft"
    data-test={dataTest}
  >
    {icon ? (
      <IconContainer size="sm" color="secondary">
        {icon}
      </IconContainer>
    ) : null}
    <Text variant="body" className="flex-1 truncate">
      {children}
    </Text>
    {isSelected ? (
      <IconContainer size="sm" color="secondary">
        <Check />
      </IconContainer>
    ) : null}
  </button>
);

// One popover drives the whole shift-dates picker: the preset list swaps in
// place for the custom-amount panel or the calendar. Nesting a Radix menu and
// a popover instead makes the menu's dismissal close the panel on open.
const ShiftDatesDropdown: React.FC<ShiftDatesDropdownProps> = ({
  trigger,
  selection,
  onSelect,
  align = "start",
  side = "top"
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<"list" | "custom" | "calendar">("list");
  const [customAmount, setCustomAmount] = useState<number | null>(
    selection?.kind === "custom" ? selection.shift.amount : 1
  );
  const [customUnit, setCustomUnit] = useState<ShiftUnit>(
    selection?.kind === "custom" ? selection.shift.unit : "years"
  );

  const isCustomInvalid = customAmount === null || customAmount < 1;

  const presetLabel = (shift: DateShift) =>
    t(`contracts.duplicate.shiftDates.presets.${shift.unit}`, {
      count: shift.amount
    });

  const commit = (next: ShiftDatesSelection) => {
    onSelect(next);
    setIsOpen(false);
  };

  const commitCustom = () => {
    if (isCustomInvalid) return;
    commit({
      kind: "custom",
      shift: { amount: customAmount, unit: customUnit }
    });
  };

  return (
    <Popover
      open={isOpen}
      onOpenChange={(open) => {
        if (open) {
          setMode("list");
          setCustomAmount(
            selection?.kind === "custom" ? selection.shift.amount : 1
          );
          setCustomUnit(
            selection?.kind === "custom" ? selection.shift.unit : "years"
          );
        }
        setIsOpen(open);
      }}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align={align}
        side={side}
        padding={mode === "list" ? 4 : 0}
        className={cn("w-fit", mode === "list" && "w-56")}
        data-test="shift-dates-dropdown"
      >
        {mode === "list" ? (
          <>
            {SHIFT_PRESETS.map((preset) => (
              <ListItem
                key={`${preset.amount}-${preset.unit}`}
                isSelected={
                  selection?.kind === "preset" &&
                  selection.shift.amount === preset.amount &&
                  selection.shift.unit === preset.unit
                }
                onClick={() => commit({ kind: "preset", shift: preset })}
                dataTest={`shift-dates-preset-${preset.amount}-${preset.unit}`}
              >
                {presetLabel(preset)}
              </ListItem>
            ))}
            <div className="h-[1px] my-1 bg-border-border1" />
            <ListItem
              isSelected={selection?.kind === "custom"}
              icon={<CalendarIcon />}
              onClick={() => setMode("custom")}
              dataTest="shift-dates-custom"
            >
              {t("contracts.duplicate.shiftDates.custom")}
            </ListItem>
            <ListItem
              isSelected={selection?.kind === "date"}
              icon={<CalendarCheck />}
              onClick={() => setMode("calendar")}
              dataTest="shift-dates-pick-date"
            >
              {t("contracts.duplicate.shiftDates.pickStartDate")}
            </ListItem>
          </>
        ) : null}
        {mode === "custom" ? (
          <div
            className="flex flex-col gap-2 p-3 w-56"
            onKeyDown={(event) => {
              if (event.key === "Enter") commitCustom();
            }}
          >
            <Text variant="smBodySecondary">
              {t("contracts.duplicate.shiftDates.title")}
            </Text>
            <div className="flex items-center gap-2">
              <Input
                autoFocus
                type="number"
                min={1}
                value={customAmount ?? ""}
                onChange={(event) => {
                  const parsed = parseInt(event.target.value);
                  setCustomAmount(Number.isNaN(parsed) ? null : parsed);
                }}
                className="w-20"
                hasError={isCustomInvalid}
                data-test="shift-dates-custom-amount"
              />
              <SingleSelect
                fieldName="shift-dates-custom-unit"
                variant="dropdown"
                mainContainerClassName="flex-1"
                options={SHIFT_UNITS.map((unit) => ({
                  id: unit,
                  displayName: t(`contracts.duplicate.shiftDates.units.${unit}`)
                }))}
                selectedValue={customUnit}
                onSelection={(value) => setCustomUnit(value as ShiftUnit)}
              />
            </div>
            {isCustomInvalid ? (
              <Text variant="smBody" className="text-error">
                {t("contracts.duplicate.shiftDates.invalidNumber")}
              </Text>
            ) : null}
            <div className="flex items-center justify-end gap-2 pt-1">
              <Button
                variant="secondary"
                onClick={() => setMode("list")}
                data-test="shift-dates-custom-cancel"
              >
                {t("cancel")}
              </Button>
              <Button
                variant="primary"
                disabled={isCustomInvalid}
                onClick={commitCustom}
                data-test="shift-dates-custom-apply"
              >
                {t("contracts.duplicate.shiftDates.apply")}
              </Button>
            </div>
          </div>
        ) : null}
        {mode === "calendar" ? (
          <Calendar
            mode="single"
            selected={selection?.kind === "date" ? selection.date : undefined}
            onSelect={(date: Date | undefined) => {
              if (date) commit({ kind: "date", date });
            }}
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
};

export default ShiftDatesDropdown;
